import SKLand from "@skland/one-bridge"
import enableInlineVideo from "iphone-inline-video"

import Reg from "./reg"

export async function sleep(ms: number) {
    return new Promise((resolve) => setTimeout(resolve, ms))
}

export function getPlatform(): "h5" | "pc" | "ipad" {
    const ua = navigator.userAgent
    if (Reg.isiPad.test(ua)) {
        return "ipad"
    } else if (!Reg.isMobile.test(ua)) {
        // Not Mobile
    } else if (Reg.isiOS.test(ua) || Reg.isAndroid.test(ua)) {
        // iOS Android
        return "h5"
    }
    return "pc"
}

export function isVivoBrowser() {
    const userAgent = navigator.userAgent
    return Reg.isVivoBrowser.test(userAgent)
}

export function isMiUIBrowser() {
    const userAgent = navigator.userAgent
    return Reg.isMiUIBrowser.test(userAgent)
}

export function isQuarkBrowser() {
    const userAgent = navigator.userAgent
    return Reg.isQuarkBrowser.test(userAgent)
}

export function isHonorBrowser() {
    const userAgent = navigator.userAgent
    return userAgent.includes("bdhonorbrowser") || userAgent.includes("HONOR")
}

export function isHuaweiBrowser() {
    return window.navigator.userAgent.includes("Huawei")
}

export function isOppoBrowser() {
    return window.navigator.userAgent.includes("HeyTap")
}

export function isMobile(): boolean {
    return typeof document.body.ontouchstart !== "undefined"
}

export function isSafari() {
    return /^((?!chrome|android).)*safari/i.test(navigator.userAgent)
}

export function isWechat() {
    const ua = window.navigator.userAgent.toLowerCase()
    return ua.includes("wechat") || ua.includes("micromessenger")
}

export function isWeibo() {
    return window.navigator.userAgent.toLowerCase().includes("weibo")
}

export function needFixWritingMode() {
    return isSafari() || (isIOS() && (isWechat() || isWeibo()))
}

export function isIOS() {
    const ua = window.navigator.userAgent
    return /iPad|iPhone|iPod/.test(ua)
}

export function formatDuration(seconds: number) {
    const hours = Math.floor(seconds / 3600)
    const minutes = Math.floor((seconds - hours * 3600) / 60)
    const sec = seconds - hours * 3600 - minutes * 60

    return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(
        2,
        "0"
    )}:${String(sec).padStart(2, "0")}`
}

export function formatDateTime(ts: number) {
    const date = new Date(ts * 1000)

    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, "0")
    const day = String(date.getDate()).padStart(2, "0")

    const hour = String(date.getHours()).padStart(2, "0")
    const minute = String(date.getMinutes()).padStart(2, "0")
    const second = String(date.getSeconds()).padStart(2, "0")

    return `${year}/${month}/${day} ${hour}:${minute}:${second}`
}

let _innerWidth = 0
let _innerHeight = 0

let _fontSize = 16
let _width = 0,
    _height = 0

function generateViewportMetaContent(scale: number) {
    return `user-scalable=no, initial-scale=${scale}, maximum-scale=${scale}, minimum-scale=${scale}, width=device-width, height=device-height, viewport-fit=cover`
}
if (isOppoBrowser() || isHuaweiBrowser() || isHonorBrowser()) {
    let viewportMeta = window.document.querySelector("meta[name=viewport]")
    if (!viewportMeta) {
        viewportMeta = document.createElement("meta")
        viewportMeta.setAttribute("name", "viewport")
    }
    viewportMeta.setAttribute(
        "content",
        generateViewportMetaContent(1 / window.devicePixelRatio)
    )
}

export function handleResize(baseFontSize = 16) {
    const { innerWidth, innerHeight } = window

    const width = Math.max(innerWidth, innerHeight)
    const height = Math.min(innerWidth, innerHeight)

    _width = width
    _height = height

    if (innerWidth === _innerWidth && innerHeight === _innerHeight) {
        return
    } else {
        _innerWidth = innerWidth
        _innerHeight = innerHeight
    }

    // lock orientation
    if (innerWidth < innerHeight) {
        document.body.style.cssText = `
            position: relative;
            left: ${height}px;
            width: ${width}px;
            height: ${height}px;
            transform-origin: top left;
            transform: rotate(90deg);
        `
        document.body.dataset.rotate = "1"
    } else {
        document.body.style.cssText = `
            width: ${width}px;
            height: ${height}px;
        `
        document.body.dataset.rotate = "0"
    }

    let fontSize = baseFontSize

    if (width / height > 16 / 9) {
        fontSize *= height / 1080
    } else {
        fontSize *= width / 1920
    }

    // set root font size
    const html = document.querySelector("html") as HTMLElement
    html.style.fontSize = `${fontSize}px`
    _fontSize = fontSize

    // font size should be scaled if system font setting is enabled
    const realFontSize = Number(
        window.getComputedStyle(html)["font-size"].replace("px", "")
    )
    if (realFontSize !== fontSize) {
        html.style.fontSize = `${(fontSize * fontSize) / realFontSize}px`
        _fontSize = (fontSize * fontSize) / realFontSize
    }

    // callback?.()

    // window.requestAnimationFrame(() => {
    //     handleResize(baseFontSize, callback)
    // })
}

const designWidth = 1920
const designHeight = 1080

export function handleResizeBySize(
    width: number,
    height: number,
    baseFontSize = 16
) {
    let fontSize = baseFontSize

    if (width / height > designWidth / designHeight) {
        fontSize *= height / designHeight
    } else {
        fontSize *= width / designWidth
    }

    // set root font size
    const html = document.querySelector("html") as HTMLElement
    html.style.fontSize = `${fontSize}px`
    _fontSize = fontSize

    const { userAgent } = window.navigator
    if (
        userAgent.includes("HarmonyOS") ||
        userAgent.includes("OpenHarmony") ||
        userAgent.includes("MQQBrowser")
    ) {
        return _fontSize
    }

    // font size should be scaled if system font setting is enabled
    const realFontSize = Number(
        window.getComputedStyle(html)["font-size"].replace("px", "")
    )
    if (realFontSize !== fontSize) {
        _fontSize = (fontSize * fontSize) / realFontSize
        html.style.fontSize = `${_fontSize}px`
    }

    return _fontSize
}

export function getPageSize() {
    return {
        width: _width,
        height: _height,
    }
}

export function getFontSize() {
    return _fontSize
}

export const loadImage = (url: string) => {
    return new Promise<HTMLImageElement>((resolve, reject) => {
        const img = new Image()
        img.crossOrigin = "anonymous"
        img.onload = () => resolve(img)
        img.onerror = reject
        img.src = url
    })
}

export const inlineVideo = (video: HTMLVideoElement) => {
    if (window.navigator.userAgent.includes("weibo")) {
        enableInlineVideo(video)
    }
}

export const getRogueUrl = (index: number) => {
    let name = ""
    switch (index) {
        case 1:
            name = __IS_PROD ? "crimsonsolitaire" : "rogue_1.html"
            break
        case 2:
            name = __IS_PROD ? "caerulaarbor" : "rogue_2.html"
            break
        case 3:
            name = __IS_PROD ? "samiexpedition" : "rogue_3.html"
            break
        case 4:
            name = __IS_PROD ? "furnacesidefables" : "rogue_4.html"
            break
        case 5:
            name = __IS_PROD ? "gardenofgrotesqueries" : "index.html"
            break
        default:
            return ""
    }

    const url = new URL(`${document.location.origin}/${name}`)
    if (SKLand.getSystemInfo().isApp) {
        url.searchParams.set("header", "0")
        if (index !== 1) {
            url.searchParams.set("ctr_orientation", "landscape")
        }
    }

    return url.toString()
}

export const getRogue5SpecialUrl = () => {
    const name = __IS_PROD ? "gardenofgrotesqueries/special" : "special.html"
    const url = new URL(`${document.location.origin}/${name}`)
    if (SKLand.getSystemInfo().isApp) {
        url.searchParams.set("header", "0")
        url.searchParams.set("ctr_orientation", "landscape")
    }

    return url.toString()
}

export const downloadImage = async (name: string, src: string) => {
    const a = document.createElement("a")
    a.download = `${name}.png`
    a.href = src
    a.click()
}

export const supportsHEVCAlpha = () => {
    const navigator = window.navigator
    const hasMediaCapabilities = !!(
        navigator.mediaCapabilities && navigator.mediaCapabilities.decodingInfo
    )
    return isSafari() && hasMediaCapabilities
}
